import { Component, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AssetCard } from '../shared/asset-card/asset-card';
import { AssetCatalogFilters } from './asset-catalog-filters/asset-catalog-filters';
import { AssetFilters } from './types';
import { DEFAULT_FILTERS, DEFAULT_SORTED_ASSETS } from './constants';
import { AssetService } from '../services/asset-service/asset.service';
import { Loader } from '../shared/loader';
import { objectEntries } from '../shared/utils';
import { AssetType } from '../services/asset-service/types';
import { ActivatedRoute, Router } from '@angular/router';
import { parseUrlToAssetFilters } from './utils';
import { map } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
  selector: 'asset-catalog-page',
  imports: [CommonModule, FormsModule, AssetCard, AssetCatalogFilters, Loader],
  templateUrl: './asset-catalog-page.html',
  styleUrl: './asset-catalog-page.scss',
})
export class AssetCatalogPage {
  assetService = inject(AssetService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);

  urlSignal = toSignal(
    this.route.queryParamMap.pipe(map((params) => parseUrlToAssetFilters(params))),
    {
      initialValue: DEFAULT_FILTERS,
    },
  );

  // Mocks Writable signal interface with set and update
  assetListFilters = Object.assign(this.urlSignal, {
    update: (updaterFn: (prev: AssetFilters) => AssetFilters) => {
      const currentFilters = this.urlSignal();
      const updatedFilters = updaterFn(currentFilters);
      this.syncFiltersToUrl(updatedFilters);
    },
    set: (value: AssetFilters) => {
      this.syncFiltersToUrl(value);
    },
  });

  // Serializer helper to clean up empty keys/arrays in the address bar
  private syncFiltersToUrl(filters: AssetFilters) {
    const queryParams: Record<string, any> = {};

    objectEntries(filters).forEach(([key, value]) => {
      // null strips empty strings/arrays
      if (Array.isArray(value)) {
        queryParams[key] = value.length > 0 ? value : null;
      } else {
        queryParams[key] = value || null;
      }
    });

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: 'merge',
    });
  }

  assetsList = this.assetService.getAssetCatalogList(this.assetListFilters);

  readonly typeOptions: string[] = [
    'All',
    'Dataset',
    'Dashboard',
    'AI Agent',
    'AI Tool',
    'Platform',
    'API',
  ];

  get searchValue(): string {
    return this.assetListFilters().search_text;
  }
  set searchValue(v: string) {
    this.assetListFilters.update((prev) => {
      const updated: AssetFilters = {
        ...prev,
        search_text: v,
      };
      return updated;
    });
  }

  readonly hasFilters = computed(() => {
    return objectEntries(this.assetListFilters()).some(([, v]) => v.length > 0);
  });

  readonly sortedAssets = computed(() => {
    const mapObject = this.assetsList.value()?.items.reduce((acc, item) => {
      const updatedAssetTypeItems = [...acc[item.asset_type], item];
      return {
        ...acc,
        [item.asset_type]: updatedAssetTypeItems,
      };
    }, DEFAULT_SORTED_ASSETS);
    return mapObject !== undefined ? objectEntries(mapObject) : undefined;
  });

  clearFilters = () => {
    this.assetListFilters.set(DEFAULT_FILTERS);
  };

  browseNew() {
    this.clearFilters();
    this.assetListFilters.update((prev) => {
      const updated: AssetFilters = {
        ...prev,
        search_text: 'new',
      };
      return updated;
    });
  }

  handleSectionSeeAllClick = (assetType: AssetType) => {
    this.assetListFilters.update((prev) => {
      return {
        ...prev,
        asset_type: [assetType],
      };
    });
  };
}
